import {
  SalesforceIntegrationId,
  SalesforceResourceCreatePayload,
  SalesforceSyncTypes,
} from 'module/api/integrations/salesforce/types';
import { IntegrationStatusTypes } from 'module/api/integrations/salesforce/types';
import { useTranslation } from 'module/hooks/use-translation';
import { showToast } from 'module/ui/components/molecules/toasts/toasts';
import { ToastType } from 'module/ui/components/molecules/toasts/types';
import {
  useSalesforceConfigByIdQuery,
  useSalesForceCreateResourceMutation,
} from 'queries/salesforce';
import { useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { getSalesforceBaseUrl } from 'components/pages/integrations/salesforce/utils/urls';
import { tv } from 'tailwind-variants';
import { SalesforceTabs } from 'components/molecules/integrations/salesforce/tabs';
import { SalesforceTabsType } from 'components/molecules/integrations/salesforce/tabs';
import { SalesforceLayout } from 'components/pages/integrations/salesforce/salesforce-layout';
import { Header } from './header';
import { AddResourceModal } from './modal';
import { ResourceMappingList } from './resource-mapping-list';
import { SidebarNavigation } from './sidebar-navigation';
import { useFieldMappingActions } from './use-field-mapping-actions';

const layoutVariants = tv({
  slots: {
    container: 'flex flex-col w-full h-full',
    contentArea: 'flex gap-2 items-stretch relative w-full flex-1 min-h-0',
    sidebarAndContent: 'flex gap-2 items-start w-full',
    mainContent: 'flex-1 min-w-0 px-6 flex flex-col min-h-0',
  },
});

export const ResourceMappings = () => {
  const resourceCreationErrorMessage = useTranslation(
    'settings.integrations.sf.resource_mappings.object.create.error.message',
  );

  const { integrationId } = useParams<SalesforceIntegrationId>();

  const {
    mutate: createResourceMutation,
    status: salesForceResourceCreationStatus,
  } = useSalesForceCreateResourceMutation(Number(integrationId));

  const { data } = useSalesforceConfigByIdQuery(Number(integrationId), {
    enabled: integrationId !== 'new',
  });

  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedNavItem, setSelectedNavItem] = useState<
    'project' | 'company' | 'user'
  >('project');

  const {
    resources,
    resourceMappingsStatus,
    resourceTypeOptions,
    resourceTypeStatus,
    onMappingAdd,
    onMappingRemove,
    onMappingChange,
    onResourceRemove,
    onResourceAdd,
  } = useFieldMappingActions(Number(integrationId), selectedNavItem);

  const { container, contentArea, mainContent } = layoutVariants();

  const handleNavItemSelect = (item: string) => {
    const resourceTypeMap: Record<string, 'project' | 'company' | 'user'> = {
      project: 'project',
      account: 'company',
      contact: 'user',
    };
    const resourceType = resourceTypeMap[item];

    if (resourceType) {
      setSelectedNavItem(resourceType);
    }
  };

  const getSidebarItemId = (
    nativeType: 'project' | 'company' | 'user',
  ): string => {
    const reverseMap: Record<'project' | 'company' | 'user', string> = {
      project: 'project',
      company: 'account',
      user: 'contact',
    };

    return reverseMap[nativeType];
  };

  const handleSubmit = (resourceType?: string) => {
    setShowAddModal(false);

    if (resourceType) {
      const resourceObject = {
        integrationId: Number(integrationId),
        externalIntegrationType: 'salesforce',
        externalResourceType: resourceType.toString(),
        nativeResourceType: selectedNavItem,
        syncType: SalesforceSyncTypes.None,
        enabled: true,
      } as SalesforceResourceCreatePayload;

      createResourceMutation(resourceObject, {
        onSuccess: (resp) => {
          onResourceAdd(resp);
        },
        onError: () => {
          showToast({
            toastContainer: { current: document.body },
            content: resourceCreationErrorMessage,
            variant: ToastType.Error,
          });
        },
      });
    }
  };

  const handleModalCancel = () => {
    setShowAddModal(false);
  };

  if (data?.integrationStatus === IntegrationStatusTypes.Expired) {
    return (
      <Navigate
        to={`${getSalesforceBaseUrl(integrationId || '')}/settings`}
      />
    );
  }

  return (
    <SalesforceLayout
      tabs={
        <SalesforceTabs
          activeKey={SalesforceTabsType.FieldSync}
          integrationId={integrationId}
          integrationStatus={data?.integrationStatus}
        />
      }
      actions={{}}
      content={
        <>
          <div className={container()}>
            {/* Header at the top */}
            <Header showResourceModal={setShowAddModal} />

            {/* Sidebar and content area side by side */}
            <div className={contentArea()}>
              <SidebarNavigation
                selectedItem={getSidebarItemId(selectedNavItem)}
                onItemSelect={handleNavItemSelect}
              />

              <div className={mainContent()}>
                <ResourceMappingList
                  integrationId={Number(integrationId)}
                  resourceMappingsStatus={resourceMappingsStatus}
                  resources={resources}
                  onMappingAdd={onMappingAdd}
                  onMappingRemove={onMappingRemove}
                  onMappingChange={onMappingChange}
                  onObjectRemove={onResourceRemove}
                  showResourceModal={setShowAddModal}
                />
              </div>
            </div>
          </div>

          {showAddModal ? (
            <AddResourceModal
              visibility={showAddModal}
              resources={resources}
              resourceTypeOptions={resourceTypeOptions}
              resourceTypeStatus={resourceTypeStatus}
              creationStatus={salesForceResourceCreationStatus}
              handleClose={handleModalCancel}
              handleSubmit={handleSubmit}
              selectedNativeResourceType={selectedNavItem}
            />
          ) : null}
        </>
      }
    />
  );
};
